<template>
  <div class="policyholder-form__container">
    <FormTitle title="Введіть дані страхувальника" />

    <div class="policyholder-form__fields">
      <div class="policyholder-form__field-row">
        <div class="policyholder-form__tax-id-wrapper">
          <PolicyholderTaxNumberSelector
            class="policyholder-form__field"
            :placeholder="policyholder.withoutTaxNumber ? 'ІПН' : 'ІПН *'"
            :is-disabled="policyholder.withoutTaxNumber"
            :limit="10"
            v-model="policyholder.taxNumber"
            :error-messages="getValidationErrors('policyholder.taxNumber', {
              requiredIf: 'ІПН обов\'язковий до заповнення',
              minLength: 'Довжина ІПН коду не може бути менше 10 цифр',
              maxLength: 'Довжина ІПН коду не може бути більше 10 цифр',
              validChecksum: 'Не правильний ІПН код',
              validTaxNumberBirthday: 'ІПН та дата народження не співпадають',
            })"
            @change="$emit('changePolicyholderTaxNumber', $event)"
            @select="$emit('changePolicyholder', $event)"
            @blur="touchField('policyholder.taxNumber')"
          />
          <HelpTooltip
            class="policyholder-form__tax-id-tooltip"
            image="https://ukasko.ua/images/tooltips/inn.png"
            image-alt="Ідентифікаційний номер"
          />
        </div>
        <PolicyholderLastNameSelector
          class="policyholder-form__field"
          placeholder="Прізвище *"
          :limit="30"
          v-model="policyholder.lastName"
          :error-messages="getValidationErrors('policyholder.lastName', {
            required: 'Прізвище обов\'язкове до заповнення',
            minLength: 'Довжина не може бути менше 2 символів',
          })"
          @change="$emit('changePolicyholderLastName', $event)"
          @select="$emit('changePolicyholder', $event)"
          @blur="touchField('policyholder.lastName')"
        />
      </div>
      <div
        v-if="taxNumberStatus"
        class="policyholder-form__field-row"
      >
        <Checkbox
          class="policyholder-form__without-tax-number-checkbox"
          label="Відсутній ІПН"
          v-model="policyholder.withoutTaxNumber"
          @change="$emit('changeWithoutTaxNumber', $event)"
        />
      </div>

      <div class="policyholder-form__field-row">
        <TextInput
          class="policyholder-form__field"
          placeholder="Імʼя *"
          :limit="30"
          :mask="getNameMask"
          v-model="policyholder.firstName"
          :error-messages="getValidationErrors('policyholder.firstName', {
            required: 'Ім\'я обов\'язкове до заповнення',
            minLength: 'Довжина не може бути менше 2 символів',
          })"
          @change="$emit('changePolicyholderFirstName', $event)"
          @blur="touchField('policyholder.firstName')"
        />
        <TextInput
          class="policyholder-form__field"
          placeholder="Імʼя по батькові *"
          :limit="30"
          :mask="getNameMask"
          v-model="policyholder.middleName"
          :error-messages="getValidationErrors('policyholder.middleName', {
            required: 'Ім\'я по батькові обов\'язкове до заповнення',
            minLength: 'Довжина не може бути менше 2 символів',
          })"
          @change="$emit('changePolicyholderMiddleName', $event)"
          @blur="touchField('policyholder.middleName')"
        />
      </div>

      <div class="policyholder-form__field-row">
        <DateInput
          class="policyholder-form__field"
          placeholder="Дата народження *"
          format="DD.MM.YYYY"
          type="input"
          :disabled-future-dates="true"
          v-model="policyholder.birthday"
          :error-messages="getValidationErrors('policyholder.birthday', {
            required: 'Дата народження обов\'язкова до заповнення',
            validAge: 'Страхувальнику має бути більше 18 та менше 100 років',
          })"
          @change="$emit('changePolicyholderBirthday', $event)"
          @blur="touchField('policyholder.birthday')"
        />
        <CitySelector
          class="policyholder-form__field"
          placeholder="Місто *"
          v-model="policyholder.address.city"
          :error-messages="getValidationErrors('policyholder.address.city.id', {
            required: 'Місто обов\'язкове до заповнення',
          })"
          @change="$emit('changePolicyholderAddressCity', $event)"
          @blur="touchField('policyholder.address.city')"
        />
      </div>

      <div class="policyholder-form__field-row">
        <TextInput
          class="policyholder-form__field"
          placeholder="Вулиця *"
          :limit="50"
          v-model="policyholder.address.street"
          :error-messages="getValidationErrors('policyholder.address.street', {
            required: 'Вулиця обов\'язкова до заповнення',
            minLength: 'Довжина не може бути менше 2 символів',
          })"
          @change="$emit('changePolicyholderAddressStreet', $event)"
          @blur="touchField('policyholder.address.street')"
        />
        <div class="policyholder-form__address-group">
          <TextInput
            class="policyholder-form__field"
            placeholder="Номер будинку *"
            :limit="10"
            v-model="policyholder.address.house"
            :error-messages="getValidationErrors('policyholder.address.house', {
              required: 'Номер будинку обов\'язковий до заповнення',
              minLength: 'Довжина не може бути менше 2 символів',
            })"
            @change="$emit('changePolicyholderAddressHouse', $event)"
            @blur="touchField('policyholder.address.house')"
          />
          <TextInput
            class="policyholder-form__field"
            placeholder="Номер квартири"
            :limit="10"
            v-model="policyholder.address.apartment"
            @change="$emit('changePolicyholderAddressApartment', $event)"
            @blur="touchField('policyholder.address.apartment')"
          />
        </div>
      </div>

      <div class="policyholder-form__field-row">
        <TextInput
          class="policyholder-form__field"
          placeholder="Номер телефона *"
          v-model="policyholder.phone"
          :mask="getUkrainianPhoneMask"
          :formatter="getUkrainianPhoneNumbersFormatter"
          :error-messages="getValidationErrors('policyholder.phone', {
            required: 'Номер телефона обов\'язковий до заповнення',
            validPhone: 'Введіть номер телефона в форматі +38 (0XX) XXX XX XX',
          })"
          @change="$emit('changePolicyholderPhone', $event)"
          @blur="touchField('policyholder.phone')"
        />
        <TextInput
          class="policyholder-form__field"
          placeholder="Email на який отримаєте поліс *"
          :limit="254"
          v-model="policyholder.email"
          :error-messages="getValidationErrors('policyholder.email', {
            required: 'Email обов\'язковий до заповнення',
            validEmail: 'Введіть правильну email адресу',
          })"
          @change="$emit('changePolicyholderEmail', $event)"
          @blur="touchField('policyholder.email')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { maxLength, minLength, required, requiredIf } from 'vuelidate/lib/validators';
import {
  CitySelector,
  PolicyholderLastNameSelector,
  PolicyholderTaxNumberSelector,
} from '@/features/selectors';
import { ValidateForm } from '@/shared/lib/form';
import { PhoneNumberFormatter, PhoneNumberMask } from '@/shared/lib/form';
import { AffectingFactorsValidation, PolicyholderMask, PolicyholderValidation } from '@/shared/lib/insurance';
import { Checkbox, DateInput, FormTitle, HelpTooltip, TextInput } from '@/shared/ui/form';

const { validAge: validPolicyholderAge, validUkrainianPhoneNumber, validEmail } = PolicyholderValidation();
const { validChecksum, validTaxNumberBirthday } = AffectingFactorsValidation();
const { nameMask } = PolicyholderMask();
const { ukrainianPhoneNumbersFormatter } = PhoneNumberFormatter();
const { ukrainianPhoneMask } = PhoneNumberMask();

export default {
  props: {
    policyholderTaxNumber: {
      type: [String, Number],
      default: null,
    },
    policyholderLastName: {
      type: String,
      default: null,
    },
    policyholderFirstName: {
      type: String,
      default: null,
    },
    policyholderMiddleName: {
      type: String,
      default: null,
    },
    policyholderBirthday: {
      type: [String, Date],
      default: null,
    },
    policyholderAddressCity: {
      type: Object,
      default: null,
    },
    policyholderAddressStreet: {
      type: String,
      default: null,
    },
    policyholderAddressHouse: {
      type: [String, Number],
      default: null,
    },
    policyholderAddressApartment: {
      type: [String, Number],
      default: null,
    },
    policyholderPhone: {
      type: String,
      default: null,
    },
    policyholderEmail: {
      type: String,
      default: null,
    },
    taxNumberStatus: {
      type: Boolean,
      default: false,
    },
    withoutTaxNumber: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      policyholder: {
        taxNumber: this.policyholderTaxNumber,
        withoutTaxNumber: this.withoutTaxNumber,
        firstName: this.policyholderFirstName,
        middleName: this.policyholderMiddleName,
        lastName: this.policyholderLastName,
        birthday: this.policyholderBirthday,
        address: {
          city: this.policyholderAddressCity,
          street: this.policyholderAddressStreet,
          house: this.policyholderAddressHouse,
          apartment: this.policyholderAddressApartment,
        },
        phone: this.policyholderPhone,
        email: this.policyholderEmail,
      },
    };
  },
  validations() {
    return {
      policyholder: {
        taxNumber: {
          requiredIf: requiredIf(() => !this.policyholder.withoutTaxNumber),
          minLength: minLength(10),
          maxLength: maxLength(10),
          validChecksum: validChecksum,
          validTaxNumberBirthday: validTaxNumberBirthday(this.policyholder.birthday, 'DD.MM.YYYY'),
        },
        firstName: {
          required,
          minLength: minLength(2),
        },
        middleName: {
          required,
          minLength: minLength(2),
        },
        lastName: {
          required,
          minLength: minLength(2),
        },
        birthday: {
          required,
          validAge: validPolicyholderAge,
        },
        address: {
          city: {
            id: {
              required,
            },
          },
          street: {
            required,
            minLength: minLength(2),
          },
          house: {
            required,
          },
        },
        phone: {
          required,
          validPhone: validUkrainianPhoneNumber,
        },
        email: {
          required,
          validEmail: validEmail,
        },
      },
    };
  },
  mixins: [ValidateForm],
  components: {
    FormTitle, Checkbox, TextInput, HelpTooltip, DateInput, CitySelector,
    PolicyholderTaxNumberSelector, PolicyholderLastNameSelector,
  },
  computed: {
    getUkrainianPhoneNumbersFormatter() {
      return {
        format(value) {
          return ukrainianPhoneNumbersFormatter.format(value) || '+38 (0';
        },
        revert: ukrainianPhoneNumbersFormatter.revert,
      };
    },
    getUkrainianPhoneMask() {
      return ukrainianPhoneMask;
    },
    getNameMask() {
      return nameMask;
    },
  },
  watch: {
    policyholderTaxNumber(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.taxNumber = this.policyholderTaxNumber;
      }
    },
    policyholderLastName(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.lastName = this.policyholderLastName;
      }
    },
    policyholderFirstName(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.firstName = this.policyholderFirstName;
      }
    },
    policyholderMiddleName(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.middleName = this.policyholderMiddleName;
      }
    },
    policyholderBirthday(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.birthday = this.policyholderBirthday;
      }
    },
    policyholderAddressCity(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.address.city = this.policyholderAddressCity;
      }
    },
    policyholderAddressStreet(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.address.street = this.policyholderAddressStreet;
      }
    },
    policyholderAddressHouse(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.address.house = this.policyholderAddressHouse;
      }
    },
    policyholderAddressApartment(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.address.apartment = this.policyholderAddressApartment;
      }
    },
    policyholderPhone(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.phone = this.policyholderPhone;
      }
    },
    policyholderEmail(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.email = this.policyholderEmail;
      }
    },
    withoutTaxNumber(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.policyholder.withoutTaxNumber = newValue;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.policyholder-form {
  &__container {
    margin-bottom: 24px;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__field-row {
    display: flex;
    gap: 24px;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 12px;
    }
  }

  &__address-group {
    display: flex;
    gap: 16px;
    flex: 1;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 12px;
    }
  }

  &__field {
    flex: 1;
  }

  &__tax-id-wrapper {
    position: relative;
    display: flex;
    flex: 1;
  }

  &__tax-id-tooltip.help-tooltip {
    position: absolute;
    top: 12px;
    right: 8px;
  }
}
</style>
